import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  Activity,
  Users,
  BellRing,
  Settings,
  KeyRound,
  LogOut,
} from "lucide-react";
import { LanguageSwitcher, useI18n } from "@faceheart/i18n";
import { useAuth } from "../../lib/auth/AuthProvider";
import { useEventStream } from "../../hooks/useEventStream";
import { ChangePasswordDialog } from "../ChangePasswordDialog";

// Nav entries; the labelKey is resolved against the admin namespace so a
// language switch immediately re-renders the sidebar.
//
// adminOnly entries surface measurement data (dashboard KPIs, health overview,
// alerts) which the backend denies to site_admin at the role level (ADR-027 —
// 403). We hide them so a site_admin sees a coherent app (員工 + 設定) instead
// of nav items that all lead to "access denied".
const NAV = [
  { to: "/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard, adminOnly: true },
  { to: "/overview", labelKey: "nav.overview", icon: Activity, adminOnly: true },
  { to: "/employees", labelKey: "nav.employees", icon: Users, adminOnly: false },
  { to: "/alerts", labelKey: "nav.alerts", icon: BellRing, adminOnly: true },
  { to: "/settings", labelKey: "nav.settings", icon: Settings, adminOnly: false },
] as const;

export function AdminLayout() {
  const { user, logout } = useAuth();
  const { t } = useI18n("admin");
  const [showChangePw, setShowChangePw] = useState(false);
  useEventStream(); // single subscription for the whole admin session

  return (
    <div className="flex h-full bg-admin-bg bg-tech-grid text-admin-text1">
      <aside className="w-[188px] shrink-0 border-r border-admin-border bg-admin-bg2/60 backdrop-blur-sm flex flex-col">
        <div className="px-5 py-5 border-b border-admin-border">
          <div className="font-mono-display text-admin-cyan text-lg tracking-wider">
            FaceHeart
          </div>
          <div className="text-admin-text3 text-xs mt-1">Admin Console</div>
        </div>
        <nav className="flex-1 py-4 px-2 space-y-1">
          {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map(({ to, labelKey, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-admin-cyan/10 text-admin-cyan"
                    : "text-admin-text2 hover:text-admin-text1 hover:bg-admin-bg3"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {t(labelKey)}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-admin-border text-xs space-y-3">
          {/* Language switcher above the user block so it's the same height
              as the rest of the sidebar text. */}
          <LanguageSwitcher className="w-full" />
          <div>
            <div className="text-admin-text2 truncate">{user?.email ?? "—"}</div>
            <div className="text-admin-text3">
              {user?.role === "admin"
                ? t("common:labels.adminRole")
                : t("common:labels.siteAdminRole")}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => setShowChangePw(true)}
              className="flex items-center gap-2 text-admin-text2 hover:text-admin-cyan text-xs"
            >
              <KeyRound className="w-3.5 h-3.5" /> {t("settings.account.title")}
            </button>
            <button
              type="button"
              onClick={() => void logout()}
              className="flex items-center gap-2 text-admin-text2 hover:text-admin-red text-xs"
            >
              <LogOut className="w-3.5 h-3.5" /> {t("common:actions.logout")}
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>

      {showChangePw && <ChangePasswordDialog onClose={() => setShowChangePw(false)} />}
    </div>
  );
}
